import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';

interface TermsSection {
  title: string;
  content: string | React.ReactNode;
}

export const TermsOfService = () => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [openSection, setOpenSection] = useState<number | null>(null);
  const [currentLang, setCurrentLang] = useState(i18n.language);

  // Update language when URL parameter changes
  useEffect(() => {
    const lang = searchParams.get('lang');
    if (lang) {
      i18n.changeLanguage(lang).then(() => {
        setCurrentLang(lang);
        setOpenSection(null);
      }).catch(error => {
        console.error('Error changing language:', error);
      });
    }
  }, [searchParams, i18n]);

  // Force re-render when language changes
  useEffect(() => {
    setCurrentLang(i18n.language);
  }, [i18n.language]);

  const toggleSection = (index: number) => {
    setOpenSection(openSection === index ? null : index);
  };

  const termsSections: TermsSection[] = [
    {
      title: t('termsOfService.sections.eligibility.title', '1. Eligibility'),
      content: t('termsOfService.sections.eligibility.content', 'You must be at least 18 years old and legally capable of entering into a binding contract to use our Services.')
    },
    {
      title: t('termsOfService.sections.accountRegistration.title', '2. Account Registration'),
      content: t('termsOfService.sections.accountRegistration.content', 'To access certain features, you may be required to create an account. You agree to provide accurate information and to keep it up to date. You are responsible for safeguarding your login credentials and for all activities under your account.')
    },
    {
      title: t('termsOfService.sections.useOfServices.title', '3. Use of Services'),
      content: (
        <div className="space-y-4">
          <p>{t('termsOfService.sections.useOfServices.intro', 'You agree to use Echo Ratings only for lawful purposes. You may not:')}</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>{t('termsOfService.sections.useOfServices.items.reverseEngineer', 'Reverse engineer, decompile, or otherwise attempt to extract source code')}</li>
            <li>{t('termsOfService.sections.useOfServices.items.harass', 'Use the Services to harass, spam, or harm others')}</li>
            <li>{t('termsOfService.sections.useOfServices.items.automation', 'Use automation (bots/scrapers) without written consent')}</li>
          </ul>
        </div>
      )
    },
    {
      title: t('termsOfService.sections.userContent.title', '4. User Content'),
      content: (
        <div className="space-y-4">
          <p>{t('termsOfService.sections.userContent.intro', 'Our platform allows users to upload, import, or sync review content. You retain ownership of your content, but you grant Echo Ratings a license to use, store, display, and process it for the purpose of operating the Services.')}</p>
          <p>{t('termsOfService.sections.userContent.confirmation', 'You confirm you have the legal right to upload or share any data you provide through Echo Ratings.')}</p>
        </div>
      )
    },
    {
      title: t('termsOfService.sections.reviewDistribution.title', '5. Review Distribution & Integrations'),
      content: (
        <div className="space-y-4">
          <p>{t('termsOfService.sections.reviewDistribution.intro', 'Echo Ratings facilitates the distribution of user reviews across third-party platforms (e.g., Google, Yelp, Facebook). You understand that:')}</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>{t('termsOfService.sections.reviewDistribution.items.noGuarantee', 'We do not guarantee acceptance of reviews by third-party platforms')}</li>
            <li>{t('termsOfService.sections.reviewDistribution.items.policies', 'Platform policies may change and affect distribution')}</li>
            <li>{t('termsOfService.sections.reviewDistribution.items.thirdParty', 'Your use of third-party services is governed by their respective terms')}</li>
          </ul>
        </div>
      )
    },
    {
      title: t('termsOfService.sections.payment.title', '6. Payment & Subscriptions'),
      content: (
        <div className="space-y-4">
          <p>{t('termsOfService.sections.payment.intro', 'Certain features require a paid subscription. By subscribing, you agree to pay all applicable fees and taxes. Unless otherwise stated, subscriptions automatically renew unless canceled prior to the billing cycle.')}</p>
          <p>{t('termsOfService.sections.payment.pricing', 'We reserve the right to change pricing with notice.')}</p>
        </div>
      )
    },
    {
      title: t('termsOfService.sections.termination.title', '7. Termination'),
      content: t('termsOfService.sections.termination.content', 'We may suspend or terminate your access to the Services at any time for violation of these Terms. You may also cancel your account at any time.')
    },
    {
      title: t('termsOfService.sections.intellectualProperty.title', '8. Intellectual Property'),
      content: t('termsOfService.sections.intellectualProperty.content', 'All content, software, and materials provided by Echo Ratings (excluding your content) are the property of Echo Ratings or its licensors and protected by intellectual property laws.')
    },
    {
      title: t('termsOfService.sections.disclaimers.title', '9. Disclaimers'),
      content: t('termsOfService.sections.disclaimers.content', 'Echo Ratings is provided "as is" and "as available" without warranties of any kind, express or implied. We do not guarantee uninterrupted or error-free service or results from using the Services.')
    },
    {
      title: t('termsOfService.sections.limitationOfLiability.title', '10. Limitation of Liability'),
      content: t('termsOfService.sections.limitationOfLiability.content', 'To the fullest extent permitted by law, Echo Ratings shall not be liable for any indirect, incidental, special, or consequential damages, or loss of data, revenue, or profits arising out of your use of the Services.')
    },
    {
      title: t('termsOfService.sections.indemnification.title', '11. Indemnification'),
      content: t('termsOfService.sections.indemnification.content', 'You agree to indemnify and hold harmless Echo Ratings from any claims, damages, or losses resulting from your use of the Services or your violation of these Terms.')
    },
    {
      title: t('termsOfService.sections.governingLaw.title', '12. Governing Law'),
      content: t('termsOfService.sections.governingLaw.content', 'These Terms are governed by and construed in accordance with the laws of the State of [Insert State], without regard to its conflict of laws rules.')
    },
    {
      title: t('termsOfService.sections.changesToTerms.title', '13. Changes to Terms'),
      content: t('termsOfService.sections.changesToTerms.content', 'We may modify these Terms from time to time. We\'ll notify you of material changes by email or through the Service. Continued use constitutes acceptance of the revised Terms.')
    },
    {
      title: t('termsOfService.sections.contactUs.title', '14. Contact Us'),
      content: (
        <div className="space-y-2">
          <p>{t('termsOfService.sections.contactUs.intro', 'If you have questions about these Terms, please contact us at:')}</p>
          <p>{t('termsOfService.sections.contactUs.email', 'Email:')} <a href="mailto:support@echoratings.com" className="text-brand-baseBlue hover:underline">support@echoratings.com</a></p>
        </div>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-12">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {t('termsOfService.title', 'Terms of Service')}
          </h1>
          <p className="text-lg text-gray-600 mb-2">
            {t('termsOfService.effectiveDate', 'Effective Date:')} 07/29/2025
          </p>
          <p className="text-lg text-gray-600 mb-4">
            {t('termsOfService.lastUpdated', 'Last Updated:')} 07/29/2025
          </p>
          <p className="text-lg text-gray-600">
            {t('termsOfService.intro', 'Welcome to Echo Ratings ("Echo Ratings", "we", "our", or "us"). These Terms of Service ("Terms") govern your use of our website https://www.echoratings.com, web application, and related services (collectively, the "Services"). By accessing or using the Services, you agree to be bound by these Terms. If you do not agree, do not use our Services.')}
          </p>
        </div>

        <div className="space-y-4">
          {termsSections.map((section, index) => (
            <div
              key={`${index}-${currentLang}`}
              className="bg-white rounded-lg shadow-sm overflow-hidden"
            >
              <button
                className="w-full px-6 py-4 text-left focus:outline-none"
                onClick={() => toggleSection(index)}
              >
                <div className="flex justify-between items-center">
                  <h3 className="text-lg font-medium text-gray-900">
                    {section.title}
                  </h3>
                  <span className="ml-6 flex-shrink-0">
                    <svg
                      className={`h-6 w-6 transform ${
                        openSection === index ? 'rotate-180' : ''
                      } transition-transform duration-200 text-brand-baseBlue`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </div>
              </button>
              {openSection === index && (
                <div className="px-6 pb-4">
                  <div className="text-gray-600">{section.content}</div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-12 text-center space-y-4">
          <p className="text-gray-600">
            {t('termsOfService.questions', 'If you have questions about these Terms, please contact our support team at')}{' '}
            <a href="mailto:contact@echoratings.com" className="text-brand-baseBlue hover:underline">
              contact@echoratings.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};